import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Minus, Plus, X, ArrowRight } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { useCart } from "@/context/CartContext";
import { itemKey, itemPrice, itemImage, itemLabel } from "@/context/CartContext";
import { imgSrc } from "@/lib/api";

export const CartDrawer = () => {
    const { items, isOpen, closeCart, setQty, removeItem, total } = useCart();
    const navigate = useNavigate();

    const checkout = () => {
        closeCart();
        navigate("/checkout");
    };

    return (
        <Sheet open={isOpen} onOpenChange={(open) => !open && closeCart()}>
            <SheetContent side="right" data-testid="cart-drawer" className="w-full rounded-none border-l border-line bg-sand p-0 sm:max-w-md">
                <SheetHeader className="border-b border-line px-6 py-5">
                    <SheetTitle className="font-serif text-2xl font-medium tracking-tight">Your Bag</SheetTitle>
                </SheetHeader>
                <div className="flex h-[calc(100vh-180px)] flex-col overflow-y-auto px-6">
                    {items.length === 0 ? (
                        <div className="flex flex-1 flex-col items-start justify-center gap-4" data-testid="cart-empty-state">
                            <p className="font-serif text-xl italic text-clay">Your bag is empty — for now.</p>
                            <Link
                                to="/shop"
                                onClick={closeCart}
                                data-testid="cart-browse-link"
                                className="border border-espresso px-5 py-2.5 font-mono text-xs uppercase tracking-[0.2em] transition-colors duration-300 hover:bg-espresso hover:text-sand"
                            >
                                Browse the collection
                            </Link>
                        </div>
                    ) : (
                        items.map((item) => {
                            const key = itemKey(item.product.id, item.variant);
                            return (
                            <div key={key} data-testid={`cart-item-${key}`} className="flex gap-4 border-b border-line py-5">
                                <div className="h-24 w-20 shrink-0 overflow-hidden bg-line/40">
                                    <img src={imgSrc(itemImage(item))} alt={itemLabel(item)} className="h-full w-full object-cover" />
                                </div>
                                <div className="flex flex-1 flex-col justify-between">
                                    <div className="flex items-start justify-between gap-2">
                                        <div>
                                            <p className="font-serif text-lg leading-tight">{item.product.name}</p>
                                            <p className="mt-1 font-mono text-xs uppercase tracking-[0.2em] text-clay">
                                                {item.variant ? `Color — ${item.variant.name}` : item.product.category}
                                            </p>
                                        </div>
                                        <button onClick={() => removeItem(key)} data-testid={`cart-remove-${key}`} className="text-clay transition-colors hover:text-crimson">
                                            <X size={16} />
                                        </button>
                                    </div>
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center border border-line">
                                            <button onClick={() => setQty(key, item.quantity - 1)} data-testid={`cart-qty-minus-${key}`} className="px-2.5 py-1.5 transition-colors hover:bg-espresso hover:text-sand">
                                                <Minus size={12} />
                                            </button>
                                            <span className="px-3 font-mono text-xs" data-testid={`cart-qty-${key}`}>{item.quantity}</span>
                                            <button onClick={() => setQty(key, item.quantity + 1)} data-testid={`cart-qty-plus-${key}`} className="px-2.5 py-1.5 transition-colors hover:bg-espresso hover:text-sand">
                                                <Plus size={12} />
                                            </button>
                                        </div>
                                        <p className="font-mono text-sm" data-testid={`cart-item-price-${key}`}>${(itemPrice(item) * item.quantity).toFixed(2)}</p>
                                    </div>
                                </div>
                            </div>
                            );
                        })
                    )}
                </div>
                {items.length > 0 && (
                    <div className="absolute bottom-0 left-0 right-0 border-t border-line bg-sand px-6 py-5">
                        <div className="mb-4 flex items-center justify-between">
                            <span className="font-mono text-xs uppercase tracking-[0.2em] text-clay">Subtotal</span>
                            <span className="font-serif text-2xl" data-testid="cart-subtotal">${total.toFixed(2)}</span>
                        </div>
                        <button
                            onClick={checkout}
                            data-testid="checkout-button"
                            className="group flex w-full items-center justify-center gap-3 bg-espresso py-4 font-mono text-xs uppercase tracking-[0.2em] text-sand transition-colors duration-300 hover:bg-gold hover:text-espresso"
                        >
                            Checkout securely
                            <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
                        </button>
                        <p className="mt-3 text-center font-mono text-[10px] uppercase tracking-[0.2em] text-clay">Live USPS rates at checkout · International available</p>
                    </div>
                )}
            </SheetContent>
        </Sheet>
    );
};
